import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import toast from "react-hot-toast";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { uploadEmailMedia } from "../../../utils/uploadEmailMedia";

const TEMPLATE_LABELS = {
  promotion: "Promotion",
  paymentReminder: "Rappel paiement",
  failedTransaction: "Transaction échouée",
  successfulTransaction: "Transaction réussie",
  proceedPayment: "Procéder au paiement",
};

// (frontend preview only) — backend is source of truth
function localTemplatePreview({ templateKey, reference }) {
  const title = TEMPLATE_LABELS[templateKey] || templateKey;
  return `
  <div style="font-family:Arial,Helvetica,sans-serif;color:#0f172a;">
    <div style="font-weight:700;font-size:14px;margin-bottom:8px;">${title}</div>
    <div style="background:#f1f5f9;border:1px solid #e2e8f0;border-radius:12px;padding:10px;margin-bottom:10px;">
      <div style="font-size:12px;"><strong>Référence :</strong> ${reference || "-"}</div>
    </div>
    <div style="font-size:13px;line-height:1.6;">
      Ceci est un aperçu. Le rendu final envoyé est généré par le serveur.
    </div>
  </div>`;
}

export default function TransactionMessagesModal({ apiUrl, transactionId, onClose }) {
  const [tab, setTab] = useState("history"); // "history" | "send"
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  // context (recipient + reference)
  const [ctxLoading, setCtxLoading] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState(null);
  const [reference, setReference] = useState(null);

  // send form
  const [mode, setMode] = useState("template"); // "template" | "custom"
  const [templateKey, setTemplateKey] = useState("paymentReminder");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(""); // rich content for custom
  const [sending, setSending] = useState(false);

  const quillRef = useRef(null);

  const templatesOptions = useMemo(() => Object.keys(TEMPLATE_LABELS), []);

  const safeApiUrl = useMemo(() => String(apiUrl || "").replace(/\/+$/, ""), [apiUrl]);

  const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(String(s || ""));

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(
        `${safeApiUrl}/api/admin/transactions/${transactionId}/emails`
      );
      if (!res.ok) throw new Error("Erreur de récupération des emails");
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erreur lors du chargement des emails");
    } finally {
      setLoading(false);
    }
  };

  const fetchContext = async () => {
    try {
      setCtxLoading(true);
      const res = await fetchWithAuth(
        `${safeApiUrl}/api/admin/transactions/${transactionId}/email-context`
      );
      if (!res.ok) throw new Error("Erreur de récupération du contexte email");
      const data = await res.json();
      setRecipientEmail(data.recipientEmail || null);
      setReference(data.reference || String(transactionId));
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erreur lors du chargement du contexte");
      setRecipientEmail(null);
      setReference(String(transactionId));
    } finally {
      setCtxLoading(false);
    }
  };

  // lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!transactionId) return;
    fetchContext();
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeApiUrl, transactionId]);

  // esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  // Insert image with a chosen width (Option A)
  const handleInsertImage = async (widthPx = 420) => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        toast.loading("Upload image...", { id: "email-upload" });

        const url = await uploadEmailMedia(safeApiUrl, file);

        toast.success("Image uploadée", { id: "email-upload" });

        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;

        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();

        const htmlImg = `
          <p>
            <img
              src="${url}"
              alt="image"
              style="max-width:100%; width:${widthPx}px; height:auto; border-radius:8px; display:block;"
            />
          </p>
        `;

        // Use pasteHTML to preserve inline styles (best for email rendering)
        editor.clipboard.dangerouslyPasteHTML(index, htmlImg);
        editor.setSelection(index + 1);
      };
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Upload échoué");
      toast.dismiss("email-upload");
    }
  };

  const submitSend = async () => {
    if (!transactionId) return;

    if (!recipientEmail) {
      toast.error("Aucun email destinataire trouvé pour cet utilisateur.");
      return;
    }

    try {
      setSending(true);

      let payload;

      if (mode === "template") {
        payload = {
          templateKey,
          ...(subject.trim() ? { subject: subject.trim() } : {}),
        };
      } else {
        if (!subject.trim()) {
          toast.error("Le sujet est requis");
          return;
        }

        // must have some content
        const cleaned = String(html || "").replace(/<(.|\n)*?>/g, "").trim();
        if (!String(html || "").trim() || (!cleaned && !String(html || "").includes("<img"))) {
          toast.error("Le contenu de l'email est requis");
          return;
        }

        payload = {
          subject: subject.trim(),
          html, // rich html
        };
      }

      const res = await fetchWithAuth(
        `${safeApiUrl}/api/admin/transactions/${transactionId}/email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        let errText = "Erreur lors de l'envoi";
        try {
          const e = await res.json();
          errText = e?.message || errText;
        } catch {}
        throw new Error(errText);
      }

      toast.success("Email envoyé");
      setTab("history");
      setSubject("");
      setHtml("");
      await fetchEmails();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
      },
    }),
    []
  );

  // --- Portal mounting point ---
  const modalRoot =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  if (!modalRoot) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] bg-black/40 overflow-y-auto"
      onMouseDown={(e) => {
        // close only if backdrop clicked
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Add padding + allow scroll; top spacing matches header height */}
      <div className="min-h-full flex items-start justify-center p-4 md:p-8">
        <div
          className="w-full max-w-4xl mt-16 md:mt-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg relative"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Sticky header inside modal */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-lg">
            <button
              onClick={onClose}
              className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 dark:hover:text-white"
              aria-label="Fermer"
              type="button"
            >
              ✖
            </button>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Emails — Transaction #{reference}
            </h3>

            {/* Context banner */}
            <div className="mt-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
              {ctxLoading ? (
                <div className="text-sm text-gray-500">Chargement du destinataire...</div>
              ) : (
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div>
                    <span className="font-medium">Référence :</span>{" "}
                    <span className="font-mono">{reference || String(transactionId)}</span>
                  </div>
                  <div className="mt-1">
                    <span className="font-medium">Destinataire :</span>{" "}
                    <span className="font-mono">{recipientEmail || "—"}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setTab("history")}
                className={`px-3 py-2 rounded text-sm ${
                  tab === "history"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                }`}
                type="button"
              >
                Historique
              </button>
              <button
                onClick={() => setTab("send")}
                className={`px-3 py-2 rounded text-sm ${
                  tab === "send"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                }`}
                type="button"
              >
                Envoyer un email
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {/* HISTORY */}
            {tab === "history" && (
              <>
                {loading ? (
                  <p className="text-gray-500">Chargement...</p>
                ) : emails.length === 0 ? (
                  <p className="text-gray-400">Aucun email envoyé</p>
                ) : (
                  <ul className="max-h-[65vh] overflow-y-auto space-y-3 pr-1">
                    {emails.map((m) => {
                      const body = m.body || "";
                      const isHtml = looksLikeHtml(body);

                      return (
                        <li
                          key={m.id}
                          className="border rounded p-3 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                                    {m.subject || "(Sans objet)"}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    À: {m.to || "—"} •{" "}
                                    {m.createdAt
                                      ? new Date(m.createdAt).toLocaleString()
                                      : "Date inconnue"}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    onClick={() => handleCopy(body)}
                                    className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                                    type="button"
                                  >
                                    Copier
                                  </button>
                                </div>
                              </div>

                              {isHtml ? (
                                <div className="mt-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                                  <div
                                    className="prose prose-sm max-w-none dark:prose-invert"
                                    dangerouslySetInnerHTML={{ __html: body }}
                                  />
                                </div>
                              ) : (
                                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                  {body || "(Aucun contenu)"}
                                </div>
                              )}

                              {m.meta && (
                                <pre className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto">
                                  {typeof m.meta === "object"
                                    ? JSON.stringify(m.meta, null, 2)
                                    : String(m.meta)}
                                </pre>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    type="button"
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}

            {/* SEND */}
            {tab === "send" && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode("template")}
                    className={`px-3 py-2 rounded text-sm ${
                      mode === "template"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                    }`}
                    type="button"
                  >
                    Modèle
                  </button>
                  <button
                    onClick={() => setMode("custom")}
                    className={`px-3 py-2 rounded text-sm ${
                      mode === "custom"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                    }`}
                    type="button"
                  >
                    Personnalisé (Rich)
                  </button>
                </div>

                {mode === "template" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Choisir un modèle
                      </label>
                      <select
                        value={templateKey}
                        onChange={(e) => setTemplateKey(e.target.value)}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900 dark:text-white border-gray-300 dark:border-gray-700"
                      >
                        {templatesOptions.map((k) => (
                          <option key={k} value={k}>
                            {TEMPLATE_LABELS[k] || k}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Sujet (optionnel)
                      </label>
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900 dark:text-white border-gray-300 dark:border-gray-700"
                        placeholder={`NGC Financial — Transaction ${reference || ""}`}
                      />
                    </div>

                    <div className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Aperçu</div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: localTemplatePreview({ templateKey, reference }),
                        }}
                      />
                    </div>
                  </div>
                )}

                {mode === "custom" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        Sujet
                      </label>
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900 dark:text-white border-gray-300 dark:border-gray-700"
                        placeholder={`Objet de l'email (ref: ${reference || transactionId})`}
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <label className="block text-sm text-gray-700 dark:text-gray-300">
                        Contenu (rich text)
                      </label>

                      {/* Option A: image insert sizes */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleInsertImage(240)}
                          className="text-xs px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                        >
                          + Image (S)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInsertImage(420)}
                          className="text-xs px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                        >
                          + Image (M)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInsertImage(640)}
                          className="text-xs px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                        >
                          + Image (L)
                        </button>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded border border-gray-300 dark:border-gray-700 overflow-hidden">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={html}
                        onChange={setHtml}
                        modules={quillModules}
                      />
                    </div>

                    <div className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Aperçu</div>
                      <div
                        className="prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: html || "<em>Aucun contenu</em>" }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => setTab("history")}
                    className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                    type="button"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={submitSend}
                    disabled={sending || ctxLoading || !recipientEmail}
                    className={`px-4 py-2 rounded text-white ${
                      sending || ctxLoading || !recipientEmail
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    type="button"
                  >
                    {sending ? "Envoi..." : "Envoyer"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
}